import React, { useCallback, useState } from 'react';
import {
    FooterContainer,
    FooterLink,
    FooterLinksWrapper,
    FooterCopyright,
    FooterLogo,
    FooterContent,
    MobileFooterContainer,
    MobileFooterLinksWrapper,
    MobileFooterCopyright,
    LanguageDropdown,
    LanguageOption,
    LanguageSelector
} from './footer.styles';
import LogoDark from '../../assets/images/logo/logo-dark.svg';
import LogoLight from '../../assets/images/logo/logo-light.svg';
import LogoMulticolor from '../../assets/images/logo/logo-multicolor.svg';
import useIsMobile from '../../hooks/useIsMobile';
import { usePWA } from '../../context/PWAContext';
import { useTranslation } from 'react-i18next';
import MaterialIcon from '../material-icon';

interface FooterProps {
    theme?: 'dark' | 'light' | 'white';
}

const Footer: React.FC<FooterProps> = ({ theme = 'white' }) => {
    const isMobile = useIsMobile();
    const isPWA = usePWA();
    const { i18n, t } = useTranslation();
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

    const getLogo = useCallback(() => {
        if (theme === "dark") return LogoLight;
        else if (theme === "light") return LogoDark;
        return LogoMulticolor;
    }, [theme]);

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
        setShowLanguageDropdown(false);
    };

    const LanguageDropdownComponent = () => (
        <LanguageSelector themeVariant={theme} onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
            {i18n.language === 'de' ? 'Deutsch' : 'English'}
            <MaterialIcon icon="arrow_drop_down" />
            {showLanguageDropdown && (
                <LanguageDropdown themeVariant={theme}>
                    <LanguageOption 
                        themeVariant={theme} 
                        active={i18n.language === 'de'} 
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); changeLanguage('de'); }}
                    >
                        Deutsch
                    </LanguageOption>
                    <LanguageOption 
                        themeVariant={theme} 
                        active={i18n.language === 'en'} 
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); changeLanguage('en'); }}
                    >
                        English
                    </LanguageOption>
                </LanguageDropdown>
            )}
        </LanguageSelector>
    );

    if (isMobile && !isPWA) {
        return (
            <MobileFooterContainer themeVariant={theme}>
                <MobileFooterLinksWrapper>
                    {/*<FooterLink href="#" target="_blank" rel="noopener noreferrer" themeVariant={theme}>Über Uns</FooterLink>*/}
                    <FooterLink href="#" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.services')}</FooterLink>
                    <FooterLink href="#" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.contact')}</FooterLink>
                    <FooterLink href="/faq" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.faq')}</FooterLink>
                    <FooterLink href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.terms')}</FooterLink>
                    <FooterLink href="/privacy-policy" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.privacy')}</FooterLink>
                    <LanguageDropdownComponent />
                </MobileFooterLinksWrapper>
                <MobileFooterCopyright themeVariant={theme}>
                    {t('footer.copyright')}
                </MobileFooterCopyright>
            </MobileFooterContainer>
        );
    }

    return (
        <FooterContainer themeVariant={theme}>
            <FooterContent themeVariant={theme}>
                <FooterLogo src={getLogo()} alt="Managd Logo" />
                <FooterLinksWrapper>
                    {/*<FooterLink href="#" target="_blank" rel="noopener noreferrer" themeVariant={theme}>Über Uns</FooterLink>*/}
                    <FooterLink href="#" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.services')}</FooterLink>
                    <FooterLink href="#" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.contact')}</FooterLink>
                    <FooterLink href="/faq" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.faq')}</FooterLink>
                    <FooterLink href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.terms')}</FooterLink>
                    <FooterLink href="/privacy-policy" target="_blank" rel="noopener noreferrer" themeVariant={theme}>{t('footer.privacy')}</FooterLink>
                    <LanguageDropdownComponent />
                </FooterLinksWrapper>
            </FooterContent>
            <FooterCopyright themeVariant={theme}>
                {t('footer.copyright')}
            </FooterCopyright>
        </FooterContainer>
    );
};

export default Footer;
